import { Card, CardContent } from "../ui/card";

export const ProgressChart = ({ testResults, testOrgInfo }) => {
  const allTests = [
    // Foundation
    ...(testResults.orgLifecycle || []),
    ...(testResults.multiTenant || []),
    ...(testResults.countryConfig || []),
    ...(testResults.serviceWizard || []),
    ...(testResults.bookingAvailability || []),
    // Operations
    ...testResults.roles,
    ...testResults.team,
    ...testResults.assets,
    ...testResults.services,
    ...(testResults.servicesApi || []),
    ...testResults.bookings,
    ...testResults.flows,
    // Financials
    ...testResults.revenue,
    ...(testResults.revenueDashboard || []),
    ...testResults.payouts,
    ...testResults.account,
    ...testResults.payroll,
    ...(testResults.promoCodes || []),
    ...testResults.stripeConnect,
    ...(testResults.expenses || []),
    // Customers
    ...testResults.branding,
    ...testResults.waivers,
    ...testResults.reviews,
    ...testResults.checkin,
    ...testResults.leads,
    ...testResults.relations,
    ...(testResults.tickets || []),
    // Settings
    ...testResults.profile,
    ...testResults.preferences,
    ...testResults.calendar,
    ...testResults.webhooks,
    ...testResults.apiKeys,
    ...testResults.activityLog,
    ...(testResults.widgetApi || []),
    ...(testResults.currencyConsistency || []),
    ...(testResults.multiCurrencyCheckout || []),
    ...(testResults.addOnsData || []),
    ...(testResults.customerTicket || []),
    ...(testResults.integrations || []),
    // Auth Flows
    ...testResults.ownerSignin,
    ...testResults.teamSignin,
    ...testResults.signup,
    // Customer Flows
    ...testResults.widgetCheckout,
    ...testResults.waiverFlow,
    ...testResults.qrCodeScanning,
    // Enterprise: Integrity
    ...(testResults.doubleBooking || []),
    ...(testResults.feeCalculations || []),
    ...(testResults.dataConsistency || []),
    // Enterprise: Negative Paths
    ...(testResults.validation || []),
    ...(testResults.security || []),
    ...(testResults.errorHandling || []),
    // Enterprise: Automation
    ...(testResults.emailAutomation || []),
    ...(testResults.alerts || []),
    ...(testResults.retryLogic || []),
    // Enterprise: Advanced
    ...(testResults.widgetEmbed || []),
    ...(testResults.stressTest || []),
    ...(testResults.ciReadiness || []),
    // Login & Permissions
    ...(testResults.superEasyLogin || []),
    ...(testResults.promoCodePermissions || []),
    // Session Fixes (Dec 2025)
    ...(testResults.sessionEmailFormatting || []),
    ...(testResults.sessionPayNowRedirect || []),
    ...(testResults.sessionCapacityCalculation || []),
    ...(testResults.sessionUICalendar || []),
    // Stripe Connect Sync & Refunds (Dec 2025)
    ...(testResults.stripeConnectSync || []),
    ...(testResults.refundSystem || []),
    // Payment Security (CRITICAL - Dec 2025)
    ...(testResults.paymentSecurity || []),
    // Timeclock System (Dec 2025)
    ...(testResults.timeclockSystem || []),
    // 2FA Security (Dec 2025)
    ...(testResults.twoFactorSecurity || []),
    // Direct Charges, Refund Security, Multiple Refunds, Email Notifications, Data Migration (Dec 2025)
    ...(testResults.directCharges || []),
    ...(testResults.refundSecurity || []),
    ...(testResults.multipleRefunds || []),
    ...(testResults.emailNotifications || []),
    ...(testResults.dataMigration || []),
    // Advanced Security & Hardening (Dec 2025)
    ...(testResults.rbacMatrix || []),
    ...(testResults.webhookSecurity || []),
    ...(testResults.bookingMutations || []),
    ...(testResults.concurrency || []),
    ...(testResults.piiSecurity || []),
    ...(testResults.tenantIsolation || []),
    // Partners & Affiliates (Dec 2025)
    ...(testResults.partnersAffiliates || []),
    ...(testResults.partnerEarnings || []),
    // Real-Time Availability (Dec 2025)
    ...(testResults.realtimeAvailability || []),
    // Widget Checkout Fixes (Dec 2025)
    ...(testResults.widgetCheckoutFixes || [])
  ];

  const total = allTests.length;
  const passed = allTests.filter(t => t.status === 'pass').length;
  const failed = allTests.filter(t => t.status === 'fail').length;
  const warnings = allTests.filter(t => t.status === 'warning').length;
  const percentage = total > 0 ? Math.round((passed / total) * 100) : 0;
  
  const circumference = 2 * Math.PI * 56;
  const passedLength = (passed / total) * circumference;
  const failedLength = (failed / total) * circumference;
  const warningsLength = (warnings / total) * circumference;
  
  // Percentage text color: green if > 0%, red if 0%
  const percentageColor = percentage > 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="space-y-4">
      {testOrgInfo && (
        <div className="flex items-center gap-4 text-sm">
          <p className="text-cyan-600 font-medium">
            Testing on: {testOrgInfo.name} ({testOrgInfo.slug})
          </p>
        </div>
      )}

      <Card className="shadow-none bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-8">
            {/* Circle Chart with Warnings */}
            <div className="relative w-32 h-32">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#e5e7eb"
                  strokeWidth="10"
                  fill="none"
                />
                {/* Green segment (passed) */}
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#10b981"
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${passedLength} ${circumference}`}
                  strokeLinecap="round"
                />
                {/* Yellow segment (warnings) */}
                {warnings > 0 && (
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#eab308"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={`${warningsLength} ${circumference}`}
                    strokeDashoffset={-passedLength}
                    strokeLinecap="round"
                  />
                )}
                {/* Red segment (failed) */}
                {failed > 0 && (
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#ef4444"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={`${failedLength} ${circumference}`}
                    strokeDashoffset={-(passedLength + warningsLength)}
                    strokeLinecap="round"
                  />
                )}
              </svg>
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${percentageColor}`}>
                    {percentage}%
                  </div>
                  <div className="text-xs text-gray-600">Health</div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {passed}
                </div>
                <div className="text-sm text-gray-600">Passed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">
                  {failed}
                </div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {warnings}
                </div>
                <div className="text-sm text-gray-600">Warnings</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
